/**
 * CDN Utility for Cloudflare R2 Assets
 *
 * Serves static assets from Cloudflare's global CDN
 * Only applies to /public folder assets, not _next/static
 */

const CDN_URL = process.env.NEXT_PUBLIC_CDN_URL || 'https://assets.marketbetter.ai';

/**
 * Get the CDN URL for a public asset
 *
 * @param path - Path to asset (e.g., '/icons/logo.svg', '/images/hero.png')
 * @returns Full CDN URL or local path if CDN is not configured
 *
 * @example
 * ```tsx
 * <img src={getCDNUrl('/icons/logo.svg')} alt="Logo" />
 * <Image src={getCDNUrl('/images/hero.png')} alt="Hero" />
 * ```
 */
function getCDNUrl(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Don't use CDN for _next assets (they're built at runtime)
  if (normalizedPath.startsWith('/_next')) {
    return normalizedPath;
  }

  // Return CDN URL if configured, otherwise local path
  return CDN_URL ? `${CDN_URL}${normalizedPath}` : normalizedPath;
}

/**
 * Get the CDN URL for an icon
 *
 * @param iconName - Icon filename (e.g., 'logo.svg', 'analytics.svg')
 * @returns Full CDN URL to icon
 *
 * @example
 * ```tsx
 * <img src={getIconUrl('analytics.svg')} alt="Analytics" />
 * ```
 */
function getIconUrl(iconName: string): string {
  return getCDNUrl(`/icons/${iconName}`);
}

/**
 * Get the CDN URL for an image
 *
 * @param imagePath - Image path relative to /images (e.g., 'hero.png', 'products/demo.jpg')
 * @returns Full CDN URL to image
 *
 * @example
 * ```tsx
 * <img src={getImageUrl('hero.png')} alt="Hero" />
 * <img src={getImageUrl('products/demo.jpg')} alt="Demo" />
 * ```
 */
export function getImageUrl(imagePath: string): string {
  return getCDNUrl(`/images/${imagePath}`);
}

/** LPG homepage assets under /images/home/ on R2 */
export function getHomeImageUrl(relativePath: string): string {
  const path = relativePath.replace(/^\/+/, '').replace(/^home\//, '');
  return getImageUrl(`home/${path}`);
}

/**
 * Get the CDN URL for a video
 *
 * @param videoName - Video filename (e.g., 'demo.mp4')
 * @returns Full CDN URL to video
 *
 * @example
 * ```tsx
 * <video src={getVideoUrl('demo.mp4')} />
 * ```
 */
function getVideoUrl(videoName: string): string {
  return getCDNUrl(`/videos/${videoName}`);
}

export { getCDNUrl, getIconUrl, getVideoUrl };
